import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useRef } from "react";

import type { InferedInAppAdAttributesSchema, OpenShiftListItem } from "../../../Shift/Open/types";
import { type InAppAdsFlag, useInAppAdsFlag } from "../useInAppAdsFeatureFlag";
import { createShiftSignature } from "./utils";

interface InjectAdsIntoShiftsParams {
  adItemAttributes: InferedInAppAdAttributesSchema;
  shifts: OpenShiftListItem[];
  interval: number;
  maxAllowedOccurrences: number;
  maxAllowedViews: number;
  storedAdViewCount: number;
}

interface ShouldSkipAdInjectionParams {
  adsConfig: InAppAdsFlag | undefined;
  ad: NonNullable<InAppAdsFlag["ads"]>[0] | undefined;
  campaignStart: string;
  campaignEnd: string;
  storedAdViewCount: number;
  maxAllowedViews: number;
}

function outsideOfCampaignWindow(campaignStart: string, campaignEnd: string): boolean {
  const now = new Date();
  return now < new Date(campaignStart) || now > new Date(campaignEnd);
}

// Select the ad with the fewest views to rotate through available ads
function determineAdToDisplay(
  adsConfig: InAppAdsFlag,
  storedAdViewCounts: Record<string, number>
): NonNullable<InAppAdsFlag["ads"]>[0] | undefined {
  if (!adsConfig.ads?.length) {
    return undefined;
  }

  const adsWithViewCounts = adsConfig.ads.map((ad) => ({
    ...ad,
    viewCount: storedAdViewCounts[ad.attributes.appId] ?? 0,
  }));

  adsWithViewCounts.sort((a, b) => a.viewCount - b.viewCount);

  return adsWithViewCounts[0];
}

function logAdInjection(
  adAttributes: InferedInAppAdAttributesSchema,
  injectionType: "empty-state" | "append" | "interval",
  metadata?: Record<string, any>
): void {
  logEvent(APP_V2_APP_EVENTS.AD_INJECTED_INTO_SHIFTS, {
    ...adAttributes,
    injection_type: injectionType,
    timestamp: new Date().toISOString(),
    ...metadata,
  });
}

function shouldSkipAdInjection(params: ShouldSkipAdInjectionParams): boolean {
  const { adsConfig, ad, campaignStart, campaignEnd, storedAdViewCount, maxAllowedViews } = params;
  return (
    !adsConfig?.enabled ||
    !adsConfig?.ads?.length ||
    !ad ||
    outsideOfCampaignWindow(campaignStart, campaignEnd) ||
    storedAdViewCount >= maxAllowedViews
  );
}

// This function will inject ads at, and only at, the desired interval.  Meaning if we have
// an interval of 10 shifts but only 16 shifts, the ad will be injected once.  It will
// also ensure that we don't exceed the maxOccurances or maxAllowedViews.
function injectAdsIntoShifts(params: InjectAdsIntoShiftsParams): OpenShiftListItem[] {
  const {
    adItemAttributes,
    shifts,
    interval,
    maxAllowedOccurrences,
    maxAllowedViews,
    storedAdViewCount,
  } = params;
  let injectionCount = 0;
  return shifts.flatMap((shift, index) => {
    const shiftPosition = index + 1; // 1-indexed position
    const shouldInject =
      shiftPosition % interval === 0 &&
      injectionCount < maxAllowedOccurrences &&
      storedAdViewCount + injectionCount < maxAllowedViews;

    if (shouldInject) {
      injectionCount += 1;
      const startTime = shift.type === "open-shift" ? shift.attributes?.start ?? "" : "";
      const adItem: OpenShiftListItem = { ...adItemAttributes, start: startTime };

      logAdInjection(adItemAttributes, "interval", {
        shift_position: shiftPosition,
        interval,
        injection_count: injectionCount,
        max_allowed_occurrences: maxAllowedOccurrences,
        stored_view_count: storedAdViewCount,
      });

      return [shift, adItem];
    }

    return [shift];
  });
}

export function useInAppAdInjector(shifts: OpenShiftListItem[]): OpenShiftListItem[] {
  const adsConfig = useInAppAdsFlag();
  const [storedAdViewCounts] = useLocalStorage<Record<string, number>>("storedAdViewCounts", {});

  // cache shifts and ad signatures to ensure ads are appropriately sticky, but not too
  // sticky.  I.E. once rendered they stay, but will not be re-rendered if the shifts change.
  const lastResultWithAdsRef = useRef<OpenShiftListItem[] | undefined>(undefined);
  const lastShiftSignatureRef = useRef<string>("");

  const currentShiftSignature = createShiftSignature(shifts);

  // If the base shifts haven't changed and we have a cached result with ads, return it
  if (lastShiftSignatureRef.current === currentShiftSignature && lastResultWithAdsRef.current) {
    return lastResultWithAdsRef.current;
  }

  const ad = determineAdToDisplay(adsConfig, storedAdViewCounts);

  if (!ad) {
    return shifts;
  }

  // Find the stored view count for this ad
  const storedAdViewCount = storedAdViewCounts[ad.attributes.appId] ?? 0;

  // Extract all injector options with defaults
  const {
    campaignStart = "",
    campaignEnd = "",
    maxAllowedViews = 0,
    displayOnEmptyState = false,
    maxAllowedOccurrences = 0,
    interval = 0,
  } = ad.injectorOptions;

  // Check if we should skip ad injection
  if (
    shouldSkipAdInjection({
      adsConfig,
      ad,
      campaignStart,
      campaignEnd,
      storedAdViewCount,
      maxAllowedViews,
    })
  ) {
    lastResultWithAdsRef.current = undefined;
    lastShiftSignatureRef.current = "";
    return shifts;
  }

  let result: OpenShiftListItem[];

  // Display ad if we have no shifts and display on empty state is turned on.
  if (shifts.length === 0 && displayOnEmptyState) {
    logAdInjection(ad.attributes, "empty-state", {
      shifts_count: 0,
      display_on_empty_state: true,
    });
    result = [{ ...ad.attributes, start: new Date().toISOString() }];
  } else if (shifts.length > 0 && shifts.length <= interval) {
    // This array will always be small in this case, so we went with readability over optimization.
    // Any performance issues will not manifest in a UX outcome due to the small size of the array.
    const lastOpenShift = [...shifts].reverse().find((shift) => shift.type === "open-shift");
    const priorShiftStart =
      lastOpenShift?.type === "open-shift" ? lastOpenShift.attributes?.start ?? "" : "";

    logAdInjection(ad.attributes, "append", {
      shifts_count: shifts.length,
      interval,
      injection_reason: "shifts_count_below_interval",
    });

    result = [...shifts, { ...ad.attributes, start: priorShiftStart }];
  } else {
    result = injectAdsIntoShifts({
      adItemAttributes: ad.attributes,
      shifts,
      interval,
      maxAllowedOccurrences,
      maxAllowedViews,
      storedAdViewCount,
    });
  }

  // Cache the result and signature
  lastResultWithAdsRef.current = result;
  lastShiftSignatureRef.current = currentShiftSignature;

  return result;
}
